import { t, getLocale, setLocale } from "../i18n/index.js";
import { SUPPORTED_LOCALES, LOCALE_NATIVE_NAMES, type Locale } from "@dropl/shared";
import { currentSession } from "../state/session.js";
import { renderBell, wireBell } from "./notif-bell.js";
import markSvg from "../assets/mark-currentcolor.svg?raw";

// Drapeaux du sélecteur de langue (SVG locaux, flag-icons — MIT), un fichier
// par locale : `assets/flags/<locale>.svg`. Servis comme URLs hashées.
const FLAG_URLS = import.meta.glob<string>("../assets/flags/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
});

function flagImg(locale: Locale, cls: string): string {
  const url = FLAG_URLS[`../assets/flags/${locale}.svg`];
  if (!url) return "";
  return `<img class="${cls}" src="${escape(url)}" alt="" width="24" height="18" loading="lazy" decoding="async" />`;
}

interface NavItem {
  href: string;
  icon: string;
  labelKey: string;
}

function navItems(): NavItem[] {
  const { user } = currentSession();
  const isAdmin = user?.capabilities.permissions.includes("admin.access") ?? false;
  const items: NavItem[] = [
    { href: "/", icon: "fa-paper-plane", labelKey: "nav.send" },
  ];
  if (user) {
    items.push({ href: "/app", icon: "fa-user", labelKey: "nav.app" });
    if (isAdmin) items.push({ href: "/admin", icon: "fa-shield-halved", labelKey: "admin.title" });
  } else {
    items.push({ href: "/login", icon: "fa-right-to-bracket", labelKey: "nav.login" });
  }
  return items;
}

function renderNavLinks(current: string): string {
  return navItems().map((item) => {
    const active = current === item.href || (item.href !== "/" && current.startsWith(item.href));
    return `<a href="${escape(item.href)}" data-nav ${active ? 'aria-current="page"' : ""}>
      <i class="fa-solid ${escape(item.icon)}" aria-hidden="true"></i>
      <span>${escape(t(item.labelKey))}</span>
    </a>`;
  }).join("");
}

export interface HeaderOptions {

  minimal?: boolean;
}

export function renderHeader(opts: HeaderOptions = {}): string {
  const { config } = currentSession();
  const name = config.name;
  const current = typeof window !== "undefined" ? window.location.pathname : "/";
  const minimal = opts.minimal === true;

  return `
    <header class="site-header">
      <div class="container site-header__inner">
        <a href="/" data-nav class="logo" aria-label="${escape(name)}">
          <span class="logo__mark" aria-hidden="true">${markSvg}</span>
          <span class="logo__word">${escape(name)}</span>
        </a>

        ${minimal ? "" : `
        <nav class="nav nav--desktop">
          ${renderNavLinks(current)}
        </nav>`}

        <div class="site-header__actions">
          ${!minimal && currentSession().user ? renderBell() : ""}
          <div class="lang-picker" data-lang-picker>
            <button type="button" class="theme-toggle" data-lang-toggle aria-label="Language" aria-haspopup="listbox" aria-expanded="false">
              ${flagImg(getLocale() as Locale, "lang-picker__flag")}
              <span>${getLocale().toUpperCase()}</span>
              <i class="fa-solid fa-chevron-down" aria-hidden="true" style="font-size: 10px; margin-left: 2px;"></i>
            </button>
            <ul class="lang-picker__menu" role="listbox" data-lang-menu hidden>
              ${SUPPORTED_LOCALES.map((l) => `
                <li>
                  <button type="button" role="option" data-lang="${escape(l)}"
                          aria-selected="${getLocale() === l ? "true" : "false"}">
                    ${flagImg(l, "lang-picker__flag")}
                    <span class="lang-picker__code t-mono">${escape(l.toUpperCase())}</span>
                    <span class="lang-picker__name">${escape(LOCALE_NATIVE_NAMES[l])}</span>
                  </button>
                </li>`).join("")}
            </ul>
          </div>
          <button type="button" class="theme-toggle" data-theme-toggle aria-label="Theme">
            <i class="fa-solid ${currentTheme() === "dark" ? "fa-sun" : "fa-moon"}" aria-hidden="true"></i>
          </button>
          ${minimal ? "" : `
          <button type="button" class="theme-toggle nav-burger" data-burger aria-label="Menu" aria-expanded="false" aria-controls="mobile-nav">
            <i class="fa-solid fa-bars" aria-hidden="true"></i>
          </button>`}
        </div>
      </div>

      ${minimal ? "" : `
      <div class="mobile-nav" id="mobile-nav" data-mobile-nav hidden>
        <nav class="nav nav--mobile">
          ${renderNavLinks(current)}
        </nav>
      </div>`}
    </header>`;
}

function currentTheme(): "dark" | "light" {
  const set = document.documentElement.dataset.theme;
  if (set === "dark" || set === "light") return set;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function setPageTitle(title: string | null): void {
  const { config } = currentSession();
  document.title = title ? `${title} — ${config.name}` : config.name;
}

export function renderFooter(): string {
  const { config } = currentSession();
  const year = new Date().getFullYear();
  return `
    <footer class="site-footer">
      <div class="container site-footer__inner">
        <div class="site-footer__brand">
          <a href="/" class="site-footer__logo" data-nav aria-label="${escape(config.name)}">
            <img src="/favicon.svg" alt="" width="32" height="32" />
            <span class="site-footer__name">${escape(config.name)}</span>
          </a>
          <p class="site-footer__tagline">${escape(config.tagline)}</p>
        </div>
        <nav class="site-footer__cols" aria-label="${escape(t("footer.nav_aria"))}">
          <div class="site-footer__col">
            <h3 class="site-footer__col-title">${escape(t("footer.col_product"))}</h3>
            <a href="/" data-nav><i class="fa-solid fa-paper-plane" aria-hidden="true"></i>${escape(t("footer.link_send"))}</a>
            <a href="/app" data-nav><i class="fa-solid fa-folder-open" aria-hidden="true"></i>${escape(t("footer.link_app"))}</a>
            <a href="/app/api" data-nav><i class="fa-solid fa-code" aria-hidden="true"></i>${escape(t("footer.link_api"))}</a>
          </div>
          <div class="site-footer__col">
            <h3 class="site-footer__col-title">${escape(t("footer.col_legal"))}</h3>
            <a href="/legal" data-nav><i class="fa-solid fa-scale-balanced" aria-hidden="true"></i>${escape(t("footer.link_legal"))}</a>
            ${config.footerLegalPages.map((p) => `
              <a href="/legal/${escape(p.slug)}" data-nav>
                <i class="fa-solid ${escape(p.icon)}" aria-hidden="true"></i>${escape(p.title)}
              </a>`).join("")}
          </div>
          <div class="site-footer__col">
            <h3 class="site-footer__col-title">${escape(t("footer.col_contact"))}</h3>
            <a href="mailto:${escape(config.supportEmail)}" class="site-footer__mail">
              <i class="fa-solid fa-envelope" aria-hidden="true"></i>
              ${escape(config.supportEmail)}
            </a>
            ${config.discordUrl ? `
            <a href="${escape(config.discordUrl)}" target="_blank" rel="noopener noreferrer">
              <i class="fa-brands fa-discord" aria-hidden="true"></i>Discord
            </a>` : ""}
          </div>
        </nav>
      </div>
      <div class="site-footer__bar">
        <div class="container site-footer__bar-inner">
          <span>${escape(t("footer.copyright", { year: String(year), name: config.name }))}</span>
        </div>
      </div>
    </footer>`;
}

export function wireShellToggles(): void {
  wireBell();
  document.querySelector<HTMLButtonElement>("[data-theme-toggle]")?.addEventListener("click", (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const next = currentTheme() === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    localStorage.setItem("dropl:theme", next);
    const icon = btn.querySelector<HTMLElement>("i");
    if (icon) icon.className = "fa-solid " + (next === "dark" ? "fa-sun" : "fa-moon");
  });
  const langPicker = document.querySelector<HTMLElement>("[data-lang-picker]");
  const langToggle = document.querySelector<HTMLButtonElement>("[data-lang-toggle]");
  const langMenu = document.querySelector<HTMLElement>("[data-lang-menu]");
  if (langPicker && langToggle && langMenu) {
    const setOpen = (open: boolean): void => {
      langMenu.hidden = !open;
      langToggle.setAttribute("aria-expanded", String(open));
    };
    langToggle.addEventListener("click", (e) => {
      e.stopPropagation();
      setOpen(langMenu.hidden);
    });
    langMenu.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-lang]");
      if (!btn) return;
      const lang = btn.dataset.lang as Locale;
      if (!lang || lang === getLocale()) { setOpen(false); return; }
      setLocale(lang);
      location.reload();
    });
    document.addEventListener("click", (e) => {
      if (!langPicker.contains(e.target as Node)) setOpen(false);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !langMenu.hidden) setOpen(false);
    });
  }

  const burger = document.querySelector<HTMLButtonElement>("[data-burger]");
  const panel = document.querySelector<HTMLElement>("[data-mobile-nav]");
  if (burger && panel) {
    const setOpen = (open: boolean): void => {
      panel.hidden = !open;
      burger.setAttribute("aria-expanded", String(open));
      const icon = burger.querySelector("i");
      if (icon) icon.className = "fa-solid " + (open ? "fa-xmark" : "fa-bars");
      document.body.classList.toggle("nav-open", open);
    };
    burger.addEventListener("click", () => setOpen(panel.hidden));
    panel.addEventListener("click", (e) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a");
      if (a) setOpen(false);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !panel.hidden) setOpen(false);
    });
  }
}

function escape(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export { escape as escapeHtml };
